import React, { useState, useMemo, useEffect } from 'react';
import { useCurrentUser, useUserRoles, useItems, useTasks, useUserSettings, useUpdateUserSettings, useApprovals } from '../hooks/useQueries';
import LoadingSpinner from '../components/Popup/LoadingSpinner';
import Toast from '../components/Popup/Toast';
import PageHeader from '../components/Layout/PageHeader';
import DropdownFilter from '../components/Search/DropdownFilter';
import { formatItemRef } from '../utils/itemIdFormat';
import './userSettings.css';

const HomeIcon = () => <i className="bi bi-house-door"></i>;

const UserSettings = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState('overview');
  
  // Notifications settings state
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch current user data
  const { data: currentUser, isLoading: userLoading, error: userError } = useCurrentUser();
  const { data: userRoles = [], isLoading: rolesLoading } = useUserRoles();

  const currentUserEmail = currentUser?.email || null;

  // Fetch items owned by current user.
  // status = 'open' AND owner = <me>
  const { data: ownedItemsByUserId = [] } = useItems(
    currentUserEmail
      ? { status: 'open', owner: currentUserEmail }
      : { status: 'open', owner_user_id: currentUser?.id },
    { enabled: !!currentUserEmail || !!currentUser?.id }
  );

  const ownedItems = useMemo(() => {
    return Array.isArray(ownedItemsByUserId) ? ownedItemsByUserId : [];
  }, [ownedItemsByUserId]);

  // Fetch tasks assigned to current user
  const { 
    data: assignedTasks = []
  } = useTasks(
    currentUserEmail
      ? { status: 'Assigned', assigned_to: currentUserEmail }
      : { status: 'Assigned', assigned_to_user_id: currentUser?.id },
    { enabled: !!currentUserEmail || !!currentUser?.id }
  );

  // Fetch approvals so "recent activity" reflects approving items
  const { data: approvals = [] } = useApprovals({ enabled: !!currentUser?.id });

  const myApprovalActivity = useMemo(() => {
    if (!currentUser?.id || !Array.isArray(approvals)) return [];

    return approvals
      .filter(a => a?.assigned_to_user_id === currentUser.id && (a.status === 'approved' || a.status === 'denied'))
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  }, [approvals, currentUser?.id]);

  const defaultPageOptions = useMemo(() => (
    [
      { value: '/brainstorming', label: 'Brainstorming' },
      { value: '/items', label: 'Items' },
      { value: '/tasks', label: 'Tasks' },
      { value: '/approvals', label: 'Approvals' },
      { value: '/reports', label: 'Reports' }
    ]
  ), []);

  const defaultPageValue = useMemo(() => {
    const currentValue = notificationSettings?.default_page || '/brainstorming';
    return defaultPageOptions.find(o => o.value === currentValue) || defaultPageOptions[0];
  }, [defaultPageOptions, notificationSettings?.default_page]);

  const recentActivity = useMemo(() => {
    const items = (Array.isArray(ownedItems) ? ownedItems : []).map(item => ({
      type: 'item',
      id: item.id,
      date: item.created_on || item.created_at || null,
      data: item,
    }));

    const tasks = (Array.isArray(assignedTasks) ? assignedTasks : []).map(task => ({
      type: 'task',
      id: task.id,
      date: task.updated_on || task.created_on || task.created_at || null,
      data: task,
    }));

    const approvals = (Array.isArray(myApprovalActivity) ? myApprovalActivity : []).map(approval => ({
      type: 'approval',
      id: approval.id,
      date: approval.updated_at || approval.created_at || null,
      data: approval,
    }));

    return [...items, ...tasks, ...approvals]
      .filter(entry => entry.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 4);
  }, [ownedItems, assignedTasks, myApprovalActivity]);
  
  // Fetch user notification settings
  const { 
    data: settings, 
    isLoading: settingsLoading 
  } = useUserSettings();
  
  // Mutation for updating settings
  const updateUserSettings = useUpdateUserSettings();
  
  // Initialize notification settings when data loads
  useEffect(() => {
    if (settings && !notificationSettings) {
      setNotificationSettings(settings);
    }
  }, [settings, notificationSettings]);

  // Helper functions
  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };
  
  // Notification settings handlers
  const handleCheckboxChange = (field) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setHasChanges(true);
  };
  
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateUserSettings.mutateAsync(notificationSettings);
      setToast({ 
        show: true, 
        message: 'Notification settings saved successfully', 
        type: 'success' 
      });
      setHasChanges(false);
    } catch (error) {
      setToast({ 
        show: true, 
        message: 'Failed to save notification settings', 
        type: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Get primary role (assuming first role is primary)
  const primaryRole = useMemo(() => {
    return userRoles.length > 0 ? userRoles[0]?.role : 'No role assigned';
  }, [userRoles]);

  if (userLoading || rolesLoading) {
    return (
      <div className="user-settings-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (userError) {
    return (
      <div className="user-settings-page">
        <div className="error-container">
          <h3>Error Loading User Data</h3>
          <p>There was an error loading your user information. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-settings-page">
      <div className="user-settings-container">
        {/* Header */}
        <PageHeader title="User Settings" />

        {/* Tab Navigation */}
        <div className="settings-tabs">
          <button
            className={`settings-tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="bi bi-person me-2"></i>
            Overview
          </button>
          <button
            className={`settings-tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <i className="bi bi-bell me-2"></i>
            Notifications
          </button>
        </div>

        {/* Tab Content */}
        <div className="settings-tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* User Info Cards */}
              <div className="info-cards-container">
                {/* Personal Information Card */}
                <div className="info-card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="bi bi-person-fill me-2"></i>
                      Personal Information
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="info-row">
                      <div className="info-label">Full Name</div>
                      <div className="info-value">{currentUser?.name || 'Not provided'}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Email</div>
                      <div className="info-value">{currentUser?.email || 'Not provided'}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Role</div>
                      <div className="info-value">
                        <span className="role-badge">{primaryRole}</span>
                      </div>
                    </div>
                    {userRoles.length > 1 && (
                      <div className="info-row">
                        <div className="info-label">Additional Roles</div>
                        <div className="info-value">
                          {userRoles.slice(1).map((roleObj, index) => (
                            <span key={index} className="role-badge secondary">
                              {roleObj.role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statistics Card */}
                <div className="info-card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="bi bi-graph-up me-2"></i>
                      Statistics
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="stats-grid">
                      <div className="stat-item">
                        <div className="stat-number">{ownedItems.length}</div>
                        <div className="stat-label">Items Owned</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-number">{assignedTasks.length}</div>
                        <div className="stat-label">Tasks Assigned</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-number">
                          {assignedTasks.filter(task => task.due_date && new Date(task.due_date) < new Date()).length}
                        </div>
                        <div className="stat-label">Overdue Tasks</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-number">
                          {assignedTasks.filter(task => task.status === 'Open').length}
                        </div>
                        <div className="stat-label">Open Tasks</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <div className="info-cards-container">
                {/* Default Page Preference */}
                <div className="info-card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="bi bi-house-door me-2"></i>
                      Preferences
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="preference-setting">
                      <label htmlFor="defaultPage" className="preference-label">
                        Default Landing Page
                      </label>
                      <DropdownFilter
                        icon={HomeIcon}
                        value={defaultPageValue}
                        onChange={(selectedOption) => {
                          const nextValue = selectedOption?.value || '/brainstorming';
                          setNotificationSettings(prev => ({
                            ...prev,
                            default_page: nextValue
                          }));
                          setHasChanges(true);
                        }}
                        options={defaultPageOptions}
                        placeholder="Select a page"
                        isClearable={false}
                        isDisabled={settingsLoading || !notificationSettings}
                        containerStyle={{ maxWidth: '100%' }}
                      />
                      <small className="preference-help">
                        Choose which page you see when you first log in
                      </small>
                    </div>
                    {hasChanges && (
                      <button
                        className="btn-save-preferences"
                        onClick={handleSaveSettings}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Save Preferences
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Recent Activity */}
                <div className="info-card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="bi bi-clock-history me-2"></i>
                      Recent Activity
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="activity-list">
                      {recentActivity.map(entry => {
                        if (entry.type === 'item') {
                          const item = entry.data;
                          return (
                            <div key={`item-${item.id}`} className="activity-item">
                              <div className="activity-icon">
                                <i className="bi bi-folder"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">Item {formatItemRef(item.id)}: {item.title}</div>
                                <div className="activity-meta">
                                  {new Date(entry.date).toLocaleDateString()} • Status: {item.status}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (entry.type === 'task') {
                          const task = entry.data;
                          return (
                            <div key={`task-${task.id}`} className="activity-item">
                              <div className="activity-icon">
                                <i className="bi bi-check2-square"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">Task #{task.id}: {task.description}</div>
                                <div className="activity-meta">
                                  {task.due_date ? `Due ${new Date(task.due_date).toLocaleDateString()}` : new Date(entry.date).toLocaleDateString()} • Status: {task.status}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        const approval = entry.data;
                        return (
                          <div key={`approval-${approval.id}`} className="activity-item">
                            <div className="activity-icon">
                              <i className="bi bi-clipboard-check"></i>
                            </div>
                            <div className="activity-content">
                              <div className="activity-title">
                                {approval.status === 'approved' ? 'Approved' : 'Denied'} Item {formatItemRef(approval.item_id)}: {approval.item_title}
                              </div>
                              <div className="activity-meta">
                                {new Date(entry.date).toLocaleDateString()} • Stage: {approval.stage_name || 'Unknown'}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {recentActivity.length === 0 && (
                        <div className="no-activity">
                          <i className="bi bi-inbox"></i>
                          <p>No recent activity found</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div className="notifications-tab">
              {settingsLoading ? (
                <LoadingSpinner />
              ) : notificationSettings ? (
                <>
                  {/* Brainstorming Notifications */}
                  <div className="notifications-section">
                    <div className="notifications-card">
                      <div className="notifications-card-header">
                        <h3 className="section-title">
                          <i className="bi bi-lightbulb me-2"></i>
                          Brainstorming Notifications
                        </h3>
                      </div>
                      <div className="notifications-card-body">
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="brainstorm_developed_email"
                            className="notification-checkbox"
                            checked={notificationSettings.brainstorm_developed_email || false}
                            onChange={() => handleCheckboxChange('brainstorm_developed_email')}
                          />
                          <label htmlFor="brainstorm_developed_email" className="notification-label">
                            When a brainstorming card I own is developed
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="brainstorm_archived_email"
                            className="notification-checkbox"
                            checked={notificationSettings.brainstorm_archived_email || false}
                            onChange={() => handleCheckboxChange('brainstorm_archived_email')}
                          />
                          <label htmlFor="brainstorm_archived_email" className="notification-label">
                            When a brainstorming card I own is archived
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="brainstorm_business_development_email"
                            className="notification-checkbox"
                            checked={notificationSettings.brainstorm_business_development_email || false}
                            onChange={() => handleCheckboxChange('brainstorm_business_development_email')}
                          />
                          <label htmlFor="brainstorm_business_development_email" className="notification-label">
                            When a business development brainstorming card is created
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Items Notifications */}
                  <div className="notifications-section">
                    <div className="notifications-card">
                      <div className="notifications-card-header">
                        <h3 className="section-title">
                          <i className="bi bi-folder me-2"></i>
                          Item Notifications
                        </h3>
                      </div>
                      <div className="notifications-card-body">
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="item_owner_assigned_email"
                            className="notification-checkbox"
                            checked={notificationSettings.item_owner_assigned_email || false}
                            onChange={() => handleCheckboxChange('item_owner_assigned_email')}
                          />
                          <label htmlFor="item_owner_assigned_email" className="notification-label">
                            When I am assigned as owner of an item
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="item_closed_email"
                            className="notification-checkbox"
                            checked={notificationSettings.item_closed_email || false}
                            onChange={() => handleCheckboxChange('item_closed_email')}
                          />
                          <label htmlFor="item_closed_email" className="notification-label">
                            When an item I own is closed
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="item_stage_moved_email"
                            className="notification-checkbox"
                            checked={notificationSettings.item_stage_moved_email || false}
                            onChange={() => handleCheckboxChange('item_stage_moved_email')}
                          />
                          <label htmlFor="item_stage_moved_email" className="notification-label">
                            When an item I own moves stages
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Approvals Notifications */}
                  <div className="notifications-section">
                    <div className="notifications-card">
                      <div className="notifications-card-header">
                        <h3 className="section-title">
                          <i className="bi bi-clipboard-check me-2"></i>
                          Approval Notifications
                        </h3>
                      </div>
                      <div className="notifications-card-body">
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="approval_assigned_email"
                            className="notification-checkbox"
                            checked={notificationSettings.approval_assigned_email || false}
                            onChange={() => handleCheckboxChange('approval_assigned_email')}
                          />
                          <label htmlFor="approval_assigned_email" className="notification-label">
                            When an approval is assigned to me
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="approval_assigned_group_email"
                            className="notification-checkbox"
                            checked={notificationSettings.approval_assigned_group_email || false}
                            onChange={() => handleCheckboxChange('approval_assigned_group_email')}
                          />
                          <label htmlFor="approval_assigned_group_email" className="notification-label">
                            When an approval is assigned to a group I'm part of
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="approval_rejected_email"
                            className="notification-checkbox"
                            checked={notificationSettings.approval_rejected_email || false}
                            onChange={() => handleCheckboxChange('approval_rejected_email')}
                          />
                          <label htmlFor="approval_rejected_email" className="notification-label">
                            When an approval I submitted is rejected
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="approval_accepted_email"
                            className="notification-checkbox"
                            checked={notificationSettings.approval_accepted_email || false}
                            onChange={() => handleCheckboxChange('approval_accepted_email')}
                          />
                          <label htmlFor="approval_accepted_email" className="notification-label">
                            When an approval I submitted is accepted
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tasks Notifications */}
                  <div className="notifications-section">
                    <div className="notifications-card">
                      <div className="notifications-card-header">
                        <h3 className="section-title">
                          <i className="bi bi-check2-square me-2"></i>
                          Task Notifications
                        </h3>
                      </div>
                      <div className="notifications-card-body">
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="task_assigned_email"
                            className="notification-checkbox"
                            checked={notificationSettings.task_assigned_email || false}
                            onChange={() => handleCheckboxChange('task_assigned_email')}
                          />
                          <label htmlFor="task_assigned_email" className="notification-label">
                            When a task is assigned to me
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="task_due_email"
                            className="notification-checkbox"
                            checked={notificationSettings.task_due_email || false}
                            onChange={() => handleCheckboxChange('task_due_email')}
                          />
                          <label htmlFor="task_due_email" className="notification-label">
                            When a task I am assigned is coming due
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="task_overdue_email"
                            className="notification-checkbox"
                            checked={notificationSettings.task_overdue_email || false}
                            onChange={() => handleCheckboxChange('task_overdue_email')}
                          />
                          <label htmlFor="task_overdue_email" className="notification-label">
                            When a task I am assigned is overdue
                          </label>
                        </div>
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="task_comment_mention_email"
                            className="notification-checkbox"
                            checked={notificationSettings.task_comment_mention_email || false}
                            onChange={() => handleCheckboxChange('task_comment_mention_email')}
                          />
                          <label htmlFor="task_comment_mention_email" className="notification-label">
                            When I am mentioned in a task comment
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Weekly Recap */}
                  <div className="notifications-section">
                    <div className="notifications-card">
                      <div className="notifications-card-header">
                        <h3 className="section-title">
                          <i className="bi bi-calendar-week me-2"></i>
                          Weekly Recap
                        </h3>
                      </div>
                      <div className="notifications-card-body">
                        <div className="notification-option">
                          <input
                            type="checkbox"
                            id="weekly_recap_email"
                            className="notification-checkbox"
                            checked={notificationSettings.weekly_recap_email || false}
                            onChange={() => handleCheckboxChange('weekly_recap_email')}
                          />
                          <label htmlFor="weekly_recap_email" className="notification-label">
                            Receive a weekly email with my open approvals and overdue tasks (Mondays)
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  <div className="save-button-container">
                    <button
                      className="btn-save"
                      onClick={handleSaveSettings}
                      disabled={!hasChanges || isSaving}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Failed to load notification settings
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <Toast 
        show={toast.show} 
        message={toast.message} 
        type={toast.type} 
        onClose={handleCloseToast} 
      />
    </div>
  );
};

export default UserSettings;